import * as stylex from "@stylexjs/stylex";
import type { HTMLAttributes, ReactNode, TableHTMLAttributes, TdHTMLAttributes, ThHTMLAttributes } from "react";
import { recipes } from "@/design/recipes";
import "@/design/tokens.stylex";
import { colors, layout, space, text } from "@/design/tokens.stylex";

// Dense tables with a summary band (Mercury 03, 07). Rows are hairline-split,
// headers are small and quiet, figures are tabular and right-aligned, and the
// hovered or selected row is washed. The summary band sits above the table and
// holds the few figures that sum it up.

const styles = {
  scroller: {
    k7Eaqz: "xeuugli",
    kXHlph: "xw2csxc",
    $$css: true
  },
  table: {
    kzqmXN: "xh8yej3",
    kLvRdT: "x1vathgz",
    kheTzg: "x1gukg7c",
    kGuDYH: "xtu3avg",
    kcqcaj: "xss6m8b",
    kMwMTN: "xgwkh9f",
    $$css: true
  },
  th: {
    kZKoxP: "x1eryijn",
    kg3NbH: "xtft4pz",
    kt9PQ7: "xso031l",
    kfdmCh: "x1q0q8m5",
    kL6WhQ: "xaecqrk",
    kGuDYH: "x7x6yu3",
    k63SB2: "xo1l8bm",
    kMwMTN: "x1i6qc9y",
    k9WMMc: "xdpxx8g",
    khDVqt: "xuxw1ft",
    kXLuUW: "xxymvpz",
    kWkggS: "x1h1f4i9",
    $$css: true
  },
  sticky: {
    kVAEAm: "x7wzq59",
    k87sOh: "x13vifvy",
    kY2c9j: "x1vjfegm",
    $$css: true
  },
  td: {
    kZKoxP: "xdxhgn1",
    kg3NbH: "xtft4pz",
    kt9PQ7: "xso031l",
    kfdmCh: "x1q0q8m5",
    kL6WhQ: "xm50ttx",
    kXLuUW: "xxymvpz",
    $$css: true
  },
  dense: {
    kZKoxP: "x1eryijn",
    $$css: true
  },
  numeric: {
    k9WMMc: "x1hr2gdg",
    khDVqt: "xuxw1ft",
    $$css: true
  },
  muted: {
    kMwMTN: "x1i6qc9y",
    $$css: true
  },
  strong: {
    k63SB2: "x1s688f",
    $$css: true
  },
  row: {
    kWkggS: "xjbqb8w xa2g735",
    $$css: true
  },
  clickable: {
    kkrTdU: "x1ypdohk",
    $$css: true
  },
  selected: {
    kWkggS: "xlcwlql x1fns82p",
    $$css: true
  },
  total: {
    k63SB2: "x1s688f",
    kWkggS: "x19qxo9d",
    $$css: true
  },
  band: {
    kogj98: "x1ghz6dp",
    k1xSpc: "x78zum5",
    kwnvtZ: "x1a02dak",
    kGNEyG: "x1cy8zhl",
    k1C7PZ: "xl4kam3",
    khm7nJ: "x1lkujnx",
    k8WAf4: "x9end0a",
    $$css: true
  },
  figure: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x3d5knr",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  figureLabel: {
    kGuDYH: "x7x6yu3",
    kMwMTN: "x1e3avs9",
    khDVqt: "xuxw1ft",
    $$css: true
  },
  figureValue: {
    kogj98: "x1ghz6dp",
    kGuDYH: "xuxpmqx",
    k63SB2: "xk50ysn",
    kb6lSQ: "xjat59b",
    kMwMTN: "xgwkh9f",
    kcqcaj: "xss6m8b",
    khDVqt: "xuxw1ft",
    $$css: true
  },
  figureHint: {
    kogj98: "x1ghz6dp",
    kGuDYH: "x7x6yu3",
    kMwMTN: "x1i6qc9y",
    $$css: true
  },
  decimals: {
    kGuDYH: "xedp305",
    kXLuUW: "xy6e0rz",
    keTefX: "xm2jcoa",
    $$css: true
  }
};
export function Table({
  children,
  xstyle,
  ...props
}: Omit<TableHTMLAttributes<HTMLTableElement>, "className" | "style"> & {
  xstyle?: stylex.StyleXStyles;
}) {
  return <div className="xeuugli xw2csxc">
      <table {...props} {...stylex.props(styles.table, xstyle)}>
        {children}
      </table>
    </div>;
}
interface CellOptions {
  numeric?: boolean;
  muted?: boolean;
  strong?: boolean;
  dense?: boolean;
  xstyle?: stylex.StyleXStyles;
}
export function Th({
  numeric,
  sticky,
  xstyle,
  children,
  ...props
}: Omit<ThHTMLAttributes<HTMLTableCellElement>, "className" | "style"> & {
  numeric?: boolean;
  sticky?: boolean;
  xstyle?: stylex.StyleXStyles;
}) {
  return <th scope="col" {...props} {...stylex.props(styles.th, numeric && styles.numeric, sticky && styles.sticky, xstyle)}>
      {children}
    </th>;
}
export function Td({
  numeric,
  muted,
  strong,
  dense,
  xstyle,
  children,
  ...props
}: Omit<TdHTMLAttributes<HTMLTableCellElement>, "className" | "style"> & CellOptions) {
  return <td {...props} {...stylex.props(styles.td, dense && styles.dense, numeric && styles.numeric, muted && styles.muted, strong && styles.strong, xstyle)}>
      {children}
    </td>;
}
export function Tr({
  selected,
  total,
  onClick,
  xstyle,
  children,
  ...props
}: Omit<HTMLAttributes<HTMLTableRowElement>, "className" | "style"> & {
  selected?: boolean;
  total?: boolean;
  xstyle?: stylex.StyleXStyles;
}) {
  return <tr {...props} aria-selected={selected || undefined} onClick={onClick} {...stylex.props(styles.row, onClick && styles.clickable, selected && styles.selected, total && styles.total, xstyle)}>
      {children}
    </tr>;
}

/** The style for any custom cell, row or header that should match the table. */
export const tableStyles = styles;
export interface SummaryFigure {
  label: ReactNode;
  value: ReactNode;
  /** A comparison or period, e.g. "vs. 12 400 last month". */
  hint?: ReactNode;
}

/** The figures that sum up the table below them. The first leads. */
export function SummaryBand({
  figures,
  xstyle
}: {
  figures: readonly SummaryFigure[];
  xstyle?: stylex.StyleXStyles;
}) {
  return <dl {...stylex.props(styles.band, xstyle)}>
      {figures.map((figure, index) => <div key={index} className="x78zum5 xdt5ytf x3d5knr xeuugli">
          <dt className="x7x6yu3 x1e3avs9 xuxw1ft">{figure.label}</dt>
          <dd className="x1ghz6dp xuxpmqx xk50ysn xjat59b xgwkh9f xss6m8b xuxw1ft">
            {typeof figure.value === "string" ? <Figure value={figure.value} /> : figure.value}
          </dd>
          {figure.hint && <dd className="x1ghz6dp x7x6yu3 x1i6qc9y">{figure.hint}</dd>}
        </div>)}
    </dl>;
}
const DECIMALS = /^(.*\d)([.,]\d{1,2})(\D*)$/;

/**
 * A formatted number with its decimals de-emphasised: "kr 12 400,50" reads
 * "kr 12 400" with a small ",50". Values without decimals render as given.
 */
function Figure({
  value,
  xstyle
}: {
  value: string;
  xstyle?: stylex.StyleXStyles;
}) {
  const match = DECIMALS.exec(value);
  if (!match) return <span {...stylex.props(recipes.tabular, xstyle)}>{value}</span>;
  return <span {...stylex.props(recipes.tabular, xstyle)}>
      {match[1]}
      <span className="xedp305 xy6e0rz xm2jcoa">{match[2]}</span>
      {match[3]}
    </span>;
}